/**
 * Edit announcement page (GM only).
 * Uses react-md-editor for markdown content editing.
 */

import { Head, Link, router } from '@inertiajs/react';
import { useState } from 'react';
import { Editor } from '@/components/Editor/Editor';

interface Announcement {
    id: number;
    title: string;
    /** Markdown content string */
    content: string;
}

interface Props {
    announcement: Announcement;
}

export default function Edit({ announcement }: Props) {
    const [title, setTitle] = useState(announcement.title);
    const [content, setContent] = useState(announcement.content || '');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<{ title?: string; content?: string }>({});

    // Handle form submission
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        // Validate
        const newErrors: { title?: string; content?: string } = {};
        if (!title.trim()) {
            newErrors.title = 'Title is required';
        }
        if (!content.trim()) {
            newErrors.content = 'Content is required';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setIsSubmitting(true);
        setErrors({});

        router.put(`/gm/announcements/${announcement.id}`, {
            title: title.trim(),
            content,
        }, {
            onError: (errors) => {
                setErrors(errors as { title?: string; content?: string });
                setIsSubmitting(false);
            },
            onFinish: () => {
                setIsSubmitting(false);
            },
        });
    };

    // Handle delete
    const handleDelete = () => {
        if (confirm('Are you sure you want to delete this announcement? This cannot be undone.')) {
            router.delete(`/gm/announcements/${announcement.id}`);
        }
    };

    return (
        <>
            <Head title={`Edit: ${announcement.title}`} />
            <div className="min-h-screen bg-slate-900 text-white">
                <div className="max-w-4xl mx-auto px-4 py-8">
                    {/* Header */}
                    <div className="flex items-center gap-4 mb-8">
                        <Link
                            href="/announcements"
                            className="text-slate-300 hover:text-white transition-colors"
                        >
                            ← Back to Announcements
                        </Link>
                        <h1 className="text-3xl font-bold">Edit Announcement</h1>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Title */}
                        <div>
                            <label htmlFor="title" className="block text-sm font-medium text-slate-300 mb-2">
                                Title
                            </label>
                            <input
                                type="text"
                                id="title"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                placeholder="Enter announcement title..."
                                maxLength={200}
                            />
                            {errors.title && (
                                <p className="mt-2 text-sm text-red-400">{errors.title}</p>
                            )}
                        </div>

                        {/* Content */}
                        <div>
                            <label className="block text-sm font-medium text-slate-300 mb-2">
                                Content
                            </label>
                            <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
                                <Editor
                                    value={content}
                                    onChange={(value) => setContent(value)}
                                    placeholder="Write your announcement..."
                                    height={300}
                                />
                            </div>
                            {errors.content && (
                                <p className="mt-2 text-sm text-red-400">{errors.content}</p>
                            )}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="px-6 py-3 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-800 disabled:cursor-not-allowed rounded-lg font-medium transition-colors"
                                >
                                    {isSubmitting ? 'Saving...' : 'Save Changes'}
                                </button>
                                <Link
                                    href={`/announcements/${announcement.id}`}
                                    className="px-6 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg font-medium transition-colors"
                                >
                                    Cancel
                                </Link>
                            </div>
                            <button
                                type="button"
                                onClick={handleDelete}
                                className="px-6 py-3 bg-red-600 hover:bg-red-700 rounded-lg font-medium transition-colors"
                            >
                                Delete
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
}

